import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { decodeHtmlEntities } from './HtmlContent';
import {
  getPageContent,
  PAGE_PATHS,
  PAGE_CONTENT_UPDATED_EVENT,
} from '../services/pageContentService';

/**
 * True when the admin has switched a page to "Simple HTML" mode and written HTML for it.
 */
export const isRawHtmlPage = (content) =>
  Boolean(content?.use_raw_html && content?.full_content && content.full_content.trim());

const slugify = (text) =>
  text
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'section';

/**
 * Splits admin-written HTML into sections. Every heading becomes a section; headings without an
 * id get one from their text (<h2>Geographical Scope</h2> -> id="geographical-scope").
 * A lone <h1> is treated as the page title and left out. Ids written by hand are kept.
 * Returns the HTML with ids filled in, and the section list.
 */
export const sectionizeHtml = (html) => {
  const decoded = decodeHtmlEntities(html || '');
  if (!decoded || typeof DOMParser === 'undefined') return { html: decoded, sections: [] };

  const docEl = new DOMParser().parseFromString(decoded, 'text/html');
  const headings = Array.from(docEl.body.querySelectorAll('h1, h2, h3, h4, h5, h6'));
  const h1Count = headings.filter((h) => h.tagName === 'H1').length;

  const used = new Set(headings.map((h) => h.id).filter(Boolean));
  const sections = [];

  headings.forEach((h) => {
    const label = (h.textContent || '').replace(/\s+/g, ' ').trim();
    if (!label) return;
    if (h.tagName === 'H1' && h1Count === 1 && !h.id) return; // page title

    if (!h.id) {
      const base = slugify(label);
      let id = base;
      for (let n = 2; used.has(id); n++) id = `${base}-${n}`;
      h.id = id;
      used.add(id);
    }
    sections.push({ id: h.id, label, level: Number(h.tagName[1]) });
  });

  return { html: docEl.body.innerHTML, sections };
};

export const getHtmlSections = (html) => sectionizeHtml(html).sections;

/**
 * Header menu sub-items generated from Simple HTML pages, keyed by every path the page is served on.
 * Refreshes when page content is saved.
 */
export const usePageSectionMenus = () => {
  const [menus, setMenus] = useState({});

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      const result = {};
      await Promise.all(
        Object.entries(PAGE_PATHS).map(async ([pageKey, paths]) => {
          try {
            const content = await getPageContent(pageKey);
            if (!isRawHtmlPage(content)) return;
            const sections = getHtmlSections(content.full_content);
            if (sections.length === 0) return;
            // Menu lists only the main sections (top heading level); sub-sections stay in the page sidebar.
            // A lone top heading is the page's title (e.g. "Aim and Scope"), so the level below it is used.
            const levels = [...new Set(sections.map((s) => s.level))].sort((x, y) => x - y);
            const countAt = sections.reduce((acc, s) => ({ ...acc, [s.level]: (acc[s.level] || 0) + 1 }), {});
            const menuLevel = levels.find((lvl, i) => countAt[lvl] > 1 || i === levels.length - 1);
            paths.forEach((path) => {
              result[path] = sections.filter((s) => s.level === menuLevel).map((s) => ({
                id: `section_${pageKey}_${s.id}`,
                label: s.label,
                path: `${path}#${s.id}`,
              }));
            });
          } catch (e) {
            // Leave this page's menu as configured
          }
        })
      );
      if (isMounted) setMenus(result);
    };

    load();
    window.addEventListener(PAGE_CONTENT_UPDATED_EVENT, load);
    return () => {
      isMounted = false;
      window.removeEventListener(PAGE_CONTENT_UPDATED_EVENT, load);
    };
  }, []);

  return menus;
};

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const header = document.querySelector('header');
  const headerHeight = header ? header.offsetHeight : 140;
  const top = el.getBoundingClientRect().top + window.pageYOffset - (headerHeight + 20);
  window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
};

/**
 * Renders admin-written HTML exactly as written: no page template, no site typography.
 * Elements get normal browser default styles (see .raw-html-content in index.css).
 * Its headings are listed in a sidebar and in the header menu (see sectionizeHtml, usePageSectionMenus).
 * `footer` is shown under the HTML (e.g. a call-to-action button).
 */
const escapeHtml = (text) =>
  String(text).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

/**
 * `title` is shown as the page heading when the admin-written HTML has no <h1> of its own.
 */
const RawHtmlPage = ({ html, title = '', className = '', footer = null }) => {
  const pageHtml = useMemo(
    () => (title && !/<h1[\s>]/i.test(decodeHtmlEntities(html || '')) ? `<h1>${escapeHtml(title)}</h1>${html || ''}` : html),
    [html, title]
  );
  const location = useLocation();
  const navigate = useNavigate();
  const { html: sectionedHtml, sections } = useMemo(() => sectionizeHtml(pageHtml), [pageHtml]);
  const [activeId, setActiveId] = useState(null);

  // Scroll to the #anchor once the HTML is on the page
  useEffect(() => {
    if (!location.hash) return;
    const timer = setTimeout(() => scrollToSection(decodeURIComponent(location.hash.slice(1))), 120);
    return () => clearTimeout(timer);
  }, [location.hash, html]);

  // Highlight the section currently being read
  useEffect(() => {
    if (sections.length === 0) return;
    const onScroll = () => {
      const header = document.querySelector('header');
      const offset = (header ? header.offsetHeight : 140) + 40;
      let current = sections[0].id;
      sections.forEach((s) => {
        const el = document.getElementById(s.id);
        if (el && el.getBoundingClientRect().top <= offset) current = s.id;
      });
      setActiveId(current);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [sections]);

  const handleSectionClick = (e, id) => {
    e.preventDefault();
    navigate({ pathname: location.pathname, hash: `#${id}` }, { replace: true });
    scrollToSection(id);
  };

  const body = (
    <div className="min-w-0">
      <div
        className={`raw-html-content ${className}`.trim()}
        dangerouslySetInnerHTML={{ __html: sectionedHtml }}
      />
      {footer}
    </div>
  );

  if (sections.length === 0) {
    return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">{body}</div>;
  }

  const minLevel = Math.min(...sections.map((s) => s.level));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)] gap-8 items-start">
      {/* Sections Sidebar */}
      <aside className="lg:sticky lg:top-28 bg-white rounded-2xl shadow-sm border border-gray-100 p-4 lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto">
        <nav className="space-y-0.5">
          {sections.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              onClick={(e) => handleSectionClick(e, s.id)}
              style={{ paddingLeft: `${0.5 + (s.level - minLevel) * 0.875}rem` }}
              className={`block pr-2 py-2 rounded-lg text-sm leading-snug transition-colors ${
                activeId === s.id
                  ? 'bg-blue-50 text-[#03478E] font-bold'
                  : 'text-gray-600 font-medium hover:bg-gray-50 hover:text-[#03478E]'
              }`}
            >
              {s.label}
            </a>
          ))}
        </nav>
      </aside>

      {body}
    </div>
  );
};

export default RawHtmlPage;
